import { Icon, Tooltip } from '@design-system';
import { cn } from '@utils/cn';
import { useTranslation } from 'next-i18next';
import { useRef } from 'react';

type OrderCalculationProps = {
  className?: string;
  freeShipping?: boolean;
  orderValue: string;
  maxOrderValue: string;
  /** Overrides the default max-order-value label, which varies per flow. */
  maxOrderValueLabel?: string;
  shippingCost?: string;
  isEstimate: boolean;
  taxText?: string;
  taxValue?: string;
};

export const OrderCalculation = ({
  className,
  freeShipping = false,
  isEstimate,
  maxOrderValue,
  maxOrderValueLabel,
  orderValue,
  shippingCost,
  taxText,
  taxValue,
}: OrderCalculationProps) => {
  const { t } = useTranslation('checkout');
  const orderValueRef = useRef<HTMLDivElement>(null);
  const shippingPending = !shippingCost && !freeShipping;

  const resolveShippingText = () => {
    if (shippingCost) return shippingCost;
    if (freeShipping) return t('orderCalculation.shipping.free');

    return t('orderCalculation.shipping.toBeCalculated');
  };

  return (
    <div className={cn('flex w-full flex-col gap-3', className)}>
      <div className="flex flex-col gap-3 p-4">
        <div className="flex flex-row justify-between">
          <div className="flex flex-row gap-1">
            <span>{t('orderCalculation.orderValue')}</span>
            {isEstimate && (
              <div ref={orderValueRef}>
                <Icon name="ic_info" size="sm" />
                <Tooltip selectionRef={orderValueRef} parentRef={orderValueRef}>
                  {t('orderCalculation.estimate')}
                </Tooltip>
              </div>
            )}
          </div>
          <span>{orderValue}</span>
        </div>
        <div className="flex flex-row justify-between">
          <span>{t('orderCalculation.shipping.label')}</span>
          <span className={cn(shippingPending && 'text-neutral-4')}>
            {resolveShippingText()}
          </span>
        </div>
        <div className="flex flex-row justify-between">
          <span className="font-bold">
            {maxOrderValueLabel ?? t('orderCalculation.maxOrderValue')}
          </span>
          <span className="font-bold">{maxOrderValue}</span>
        </div>
        {taxText && taxValue && (
          <div className="flex flex-row justify-between">
            <span className="text-neutral-4">{taxText}</span>
            <span className="text-neutral-4">{taxValue}</span>
          </div>
        )}
      </div>
    </div>
  );
};
